import React, { useEffect, useState } from 'react';
import { Button, MenuItem, TextField, Tooltip } from '@material-ui/core';
import { useLocalizedMessage } from '../../../../util/useLocalizedMessage';
import { columnCount, columnWeights, columnPreset, resizeColumns, MAX_COLUMNS } from '../../core/layout/columns';
import styles from './PropertiesEditor.module.scss';

const WeightField = ({ value, label, disabled, onChange }) => {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);
  const commit = () => {
    const number = Number(draft);
    if (Number.isFinite(number) && number > 0) onChange(number);
    else setDraft(String(value));
  };
  return <TextField type="number" variant="outlined" size="small" fullWidth label={label} value={draft} disabled={disabled}
    inputProps={{ min: 0.01, step: 'any', 'aria-label': label }} onChange={event => setDraft(event.target.value)} onBlur={commit}
    onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); commit(); } }} />;
};

const LAYOUTS = [
  { id: 'one', weights: [1] },
  { id: 'two', weights: [1, 1] },
  { id: 'three', weights: [1, 1, 1] },
  { id: 'sidebarLeft', weights: [3, 7] },
  { id: 'sidebarRight', weights: [7, 3] },
];

const ColumnsEditor = ({ node, onChange, onLayoutChange, disabled }) => {
  const t = useLocalizedMessage();
  const label = key => t(`searchInterface.columns.${key}`);
  const count = columnCount(node);
  const weights = columnWeights(node);
  const preset = columnPreset(node);
  const activeLayout = preset === 'equalColumns' ? ['one', 'two', 'three'][count - 1] : preset;
  const title = id => id === 'sidebarLeft' || id === 'sidebarRight' ? t(`experience.design.${id}`) : label(id);
  const setWeights = next => onChange({ columns: count, template: '', ratio: next.map(String) });
  const blocked = LAYOUTS.some(layout => !resizeColumns(node, layout.weights.length, layout.weights));
  return <div data-testid="columns-editor">
    <div className={styles.columnPresets} role="group" aria-label={t('experience.design.columnLayout')}>
      {LAYOUTS.map(layout => {
        const available = Boolean(resizeColumns(node, layout.weights.length, layout.weights));
        return <Tooltip key={layout.id} title={available ? title(layout.id) : label('blocked')}>
          <span><Button size="small" fullWidth disabled={disabled || !available} aria-label={title(layout.id)}
            aria-pressed={activeLayout === layout.id} onClick={() => onLayoutChange(layout.weights)}>
            <span className={styles.columnPresetContent}><span className={styles.columnDiagram}
              style={{ gridTemplateColumns: layout.weights.map(value => `${value}fr`).join(' ') }} aria-hidden="true">
              {layout.weights.map((_, index) => <span key={index} />)}
            </span>{title(layout.id)}</span>
          </Button></span>
        </Tooltip>;
      })}
    </div>
    <p className={styles.hint} data-testid="column-layout-name">{LAYOUTS.some(layout => layout.id === activeLayout) ? title(activeLayout) : label('custom')}</p>
    {blocked && <p className={styles.hint}>{label('blocked')}</p>}
    <details className={styles.columnCustom}>
      <summary>{label('customize')}</summary>
      <div className={styles.columnWidths}>
        <TextField select label={label('count')} variant="outlined" size="small" fullWidth value={count} disabled={disabled || !weights}
          SelectProps={{ inputProps: { 'aria-label': label('count') } }} onChange={event => onChange({ columns: Number(event.target.value) })}>
          {Array.from({ length: MAX_COLUMNS }, (_, index) => index + 1).map(value => <MenuItem key={value} value={value} disabled={!resizeColumns(node, value)}>{value}</MenuItem>)}
        </TextField>
        {weights ? <>
          {weights.map((weight, index) => <WeightField key={`${node.id}:${index}`} value={weight} disabled={disabled}
            label={t('searchInterface.columns.width', { number: index + 1 })}
            onChange={value => setWeights(weights.map((item, position) => position === index ? value : item))} />)}
          <p className={styles.hint}>{label('widthHint')}</p>
        </> : <p className={styles.hint}>{label('customHint')}</p>}
      </div>
    </details>
  </div>;
};
export default ColumnsEditor;
